import { useCallback } from "react";
import { useLoadingContext } from "cerulean";
import { useSudoContext } from "./SudoContext";

/**
 * Type for errors thrown by ApiHttp when sudo authentication is required
 */
interface SudoError {
  id: "sudo_authentication_failed";
  message: string;
  statusCode: number;
  error: Error;
}

/**
 * Checks if an error response indicates sudo authentication is required
 */
export function isSudoRequired(error: unknown): error is SudoError {
  return (
    !!error &&
    typeof error === "object" &&
    "id" in error &&
    "statusCode" in error &&
    error.id === "sudo_authentication_failed" &&
    error.statusCode === 401

  );
}

/**
 * Hook that provides a wrapper for API calls that automatically handles sudo errors
 * Usage:
 *
 * const handleSudoRequest = useSudoApiHandler();
 *
 * try {
 *   await handleSudoRequest(() => currentUser.updatePassword(...));
 * } catch (error) {
 *   // Handle non-sudo errors
 * }
 */
export default function useSudoApiHandler() {
  const { establishSudo } = useSudoContext();
  const { setIsLoading } = useLoadingContext();

  return useCallback(
    async <T>(apiCall: () => Promise<T>): Promise<T> => {
      try {
        return await apiCall();
      } catch (error) {
        // Only handle sudo authentication failures
        if (!isSudoRequired(error)) {
          throw error;
        }

        setIsLoading(false); // we need to prompt for input

        const currentPath = window.location.pathname;
        await establishSudo(currentPath);
        return apiCall();
      }
    },
    [establishSudo]
  );
}
